import { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Phone, Crown, Star, Gift as GiftIcon, CheckCircle, Flash, Shield } from "iconoir-react"
import Loading from "./Loading"

const GIFT_UUID_KEY = "vibrato_gift_uuid"

const Gift = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [, setGiftStored] = useState(false)

  useEffect(() => {
    const uuid = searchParams.get("uuid")

    if (uuid) {
      // Store the gift UUID in localStorage
      localStorage.setItem(GIFT_UUID_KEY, uuid)

      // Optional: Set an expiration timestamp (e.g., 30 days from now)
      const expirationTime = Date.now() + 30 * 24 * 60 * 60 * 1000 // 30 days
      localStorage.setItem(`${GIFT_UUID_KEY}_expires`, expirationTime.toString())

      console.log("Gift UUID stored:", uuid)
      setGiftStored(true)
    }

    // Show the landing page instead of redirecting immediately
    setIsLoading(false)
  }, [searchParams])

  const handleGetStarted = () => {
    navigate("/signup", { replace: true })
  }

  const handleLogin = () => {
    navigate("/dashboard", { replace: true })
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex flex-col">
      {/* Header */}
      <div className="w-full px-4 sm:px-6 py-4 sm:py-6 bg-white/80 backdrop-blur-sm border-b border-purple-100">
        <div className="max-w-6xl mx-auto flex justify-center">
          <a
            href="https://getvibrato.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity"
          >
            <img src="/vibrato-plus-logo.svg" alt="Vibrato Plus" className="h-8 sm:h-10" />
          </a>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto text-center">
          {/* Gift Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 rounded-full font-semibold mb-6 sm:mb-8 text-base border border-purple-200 shadow-sm">
            <GiftIcon className="w-5 h-5" />
            You've received a premium gift!
          </div>

          {/* Hero Section */}
          <div className="mb-6 flex flex-col items-center justify-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              Welcome to{" "}
              <span className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 bg-clip-text text-transparent">
                Vibrato Plus
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              A friend has gifted you 30 days of our premium AI calling service. Experience unlimited calls, voice
              cloning, and exclusive features - all yours to enjoy.
            </p>

            {/* Gift Box */}
            <div className="max-w-md w-full bg-white/90 backdrop-blur-sm border border-purple-200 rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8 mx-2 shadow-xl">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-14 h-14 bg-gradient-to-r from-purple-500 via-indigo-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <div className="text-center">
                  <h3 className="font-bold text-xl text-gray-900">30 Days of Premium</h3>
                  <p className="text-purple-600 font-semibold text-lg">Vibrato Plus Access</p>
                </div>
              </div>
              <div className="text-center border-t border-purple-100 pt-4">
                <p className="text-2xl font-bold text-gray-900 mb-1">$19.99 value</p>
                <p className="text-green-600 font-bold text-lg">Completely free for you</p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="space-y-3 sm:space-y-4 mb-8 sm:mb-12">
            <button
              onClick={handleGetStarted}
              className="w-full max-w-md mx-auto py-4 px-6 sm:px-8 bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 text-white font-bold text-base sm:text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 hover:scale-105 cursor-pointer"
            >
              <GiftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="hidden sm:inline">Unlock Your Premium Access</span>
              <span className="sm:hidden">Unlock Premium Access</span>
            </button>

            <button
              onClick={handleLogin}
              className="w-full max-w-md mx-auto py-3 px-4 sm:px-6 bg-white/80 hover:bg-white text-purple-600 font-medium text-base rounded-xl border border-purple-200 shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105 backdrop-blur-sm"
            >
              <span className="hidden sm:inline">Already have an account? Log In</span>
              <span className="sm:hidden">Log In</span>
            </button>

            <p className="text-gray-600 text-sm sm:text-base">
              No credit card required • Full premium features included
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
            <div className="p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-purple-100 hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 flex items-center justify-center mb-4 mx-auto shadow-md">
                <Flash className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-base sm:text-lg">Unlimited Calls</h3>
              <p className="text-gray-700 text-sm sm:text-base">
                Make as many calls as you want with no daily limits or restrictions.
              </p>
            </div>

            <div className="p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-purple-100 hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center mb-4 mx-auto shadow-md">
                <Star className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-base sm:text-lg">Voice Cloning</h3>
              <p className="text-gray-700 text-sm sm:text-base">
                Clone your voice for truly personalized AI calls that sound just like you.
              </p>
            </div>

            <div className="p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-purple-100 hover:shadow-xl transition-all duration-300 sm:col-span-2 lg:col-span-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mb-4 mx-auto shadow-md">
                <Phone className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-base sm:text-lg">Custom Number</h3>
              <p className="text-gray-700 text-sm sm:text-base">
                Get your own dedicated phone number for professional calling.
              </p>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm text-gray-600 mt-12">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-500" />
              <span>SOC 2 Protected</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-blue-500" />
              <span>Trusted by 10,000+ users</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full px-4 sm:px-6 py-6 sm:py-8 bg-white/80 backdrop-blur-sm border-t border-purple-100">
        <div className="max-w-6xl mx-auto text-center">
          {/* CTA */}
          <div className="flex justify-center mb-6">
            <a
              href="https://getvibrato.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 text-white font-semibold text-sm rounded-lg hover:shadow-lg transition-all duration-200 hover:scale-105"
            >
              Discover Vibrato
            </a>
          </div>

          <div className="text-gray-600 text-sm sm:text-base">
            <p>© 2025 Vibrato Labs, Inc.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Utility function to retrieve the stored gift UUID
export const getGiftUuid = (): string | null => {
  const uuid = localStorage.getItem(GIFT_UUID_KEY)
  const expiration = localStorage.getItem(`${GIFT_UUID_KEY}_expires`)

  if (!uuid) return null

  // Check if the gift UUID has expired
  if (expiration && Date.now() > parseInt(expiration)) {
    localStorage.removeItem(GIFT_UUID_KEY)
    localStorage.removeItem(`${GIFT_UUID_KEY}_expires`)
    return null
  }

  return uuid
}

// Utility function to clear the stored gift UUID (call this after successful signup)
export const clearGiftUuid = (): void => {
  localStorage.removeItem(GIFT_UUID_KEY)
  localStorage.removeItem(`${GIFT_UUID_KEY}_expires`)
}

export default Gift
